import { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const schema = z.object({
  name: z.string().trim().min(2, "اسم الفصل مطلوب").max(100),
  age_group: z.string().optional(),
  capacity: z.coerce.number().int().min(1).max(100),
  teacher_id: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface ClassData {
  id: string;
  name: string;
  age_group: string | null;
  capacity: number | null;
  teacher_id: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classData: ClassData | null;
  staff: { id: string; full_name: string }[];
  onSuccess: () => void;
}

export default function EditClassDialog({ open, onOpenChange, classData, staff, onSuccess }: Props) {
  const { toast } = useToast();
  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (classData) {
      reset({
        name: classData.name,
        age_group: classData.age_group || "",
        capacity: classData.capacity || 25,
        teacher_id: classData.teacher_id || "",
      });
    }
  }, [classData, reset]);

  const onSubmit = async (data: FormData) => {
    if (!classData) return;
    const { error } = await supabase.from("classes").update({
      name: data.name,
      age_group: data.age_group || null,
      capacity: data.capacity,
      teacher_id: data.teacher_id || null,
    }).eq("id", classData.id);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "تم تعديل الفصل بنجاح" });
    onOpenChange(false);
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل الفصل</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>اسم الفصل *</Label>
            <Input {...register("name")} />
            {errors.name && <p className="text-destructive text-xs mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <Label>الفئة العمرية</Label>
            <Input {...register("age_group")} />
          </div>
          <div>
            <Label>السعة *</Label>
            <Input type="number" {...register("capacity")} />
            {errors.capacity && <p className="text-destructive text-xs mt-1">{errors.capacity.message}</p>}
          </div>
          <div>
            <Label>المعلمة</Label>
            <Select defaultValue={classData?.teacher_id || ""} onValueChange={(v) => setValue("teacher_id", v)}>
              <SelectTrigger><SelectValue placeholder="اختر المعلمة" /></SelectTrigger>
              <SelectContent>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التعديلات"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
